import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../utils/theme';

const Card = styled.div`
  background: ${props => props.active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.1)' : 'rgba(76, 206, 172, 0.15)')
    : (props.theme.palette.mode === 'dark' ? 'rgba(30, 33, 48, 0.6)' : 'rgba(255, 255, 255, 0.8)')};
  border: 1px solid ${props => props.active
    ? tokens(props.theme.palette.mode).greenAccent[500]
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  
  /* Greyed out state for inactive accounts when NOT in edit mode */
  ${props => !props.active && !props.isEditMode && `
    opacity: 0.5;
    filter: grayscale(0.8);
    pointer-events: none; /* Prevent interaction */
  `}

  /* Interactive state for inactive accounts in edit mode */
  ${props => !props.active && props.isEditMode && `
    cursor: pointer;
    &:hover {
      background: ${props.theme.palette.mode === 'dark' ? 'rgba(30, 33, 48, 0.9)' : 'rgba(255, 255, 255, 1)'};
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      border-color: ${tokens(props.theme.palette.mode).grey[400]};
    }
  `}
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const StatusBadge = styled.span`
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 99px;
  background: ${props => props.active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)')
    : tokens(props.theme.palette.mode).grey[700]};
  color: ${props => props.active
    ? '#4cceac'
    : tokens(props.theme.palette.mode).grey[300]};
  border: 1px solid ${props => props.active
    ? 'rgba(76, 175, 80, 0.5)'
    : 'transparent'};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Balance = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => tokens(props.theme.palette.mode).greenAccent[400]};
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const Btn = styled.button`
  flex: 1;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${props => props.primary
    ? tokens(props.theme.palette.mode).greenAccent[500]
    : tokens(props.theme.palette.mode).grey[600]};
  background: ${props => props.primary
    ? 'rgba(76, 206, 172, 0.2)'
    : 'transparent'};
  color: ${props => props.primary
    ? tokens(props.theme.palette.mode).greenAccent[400]
    : tokens(props.theme.palette.mode).grey[200]};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;

  &:hover {
    background: ${props => props.primary
    ? 'rgba(76, 206, 172, 0.3)'
    : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const formatCurrency = (amount, currency) => {
  if (amount === undefined || amount === null) return '$0.00';
  const code = (currency || 'USD').split(' ')[0]; // Extract 'USD' from 'USD ($)'
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  } catch (e) {
    console.error("Invalid currency code:", code);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  }
};

const AccountCard = ({ account, isActive, onActivate, onManage, theme, isEditMode, isAddCard = false, onClick, onDelete, ...props }) => {
  const colors = tokens(theme.palette.mode);

  if (isAddCard) {
    return (
      <Card
        theme={theme}
        active={true} // Force active so it's clickable
        style={{
          background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
          border: `1px dashed ${tokens(theme.palette.mode).grey[500]}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          opacity: 0.7,
          minHeight: '160px' // Match approx height of other cards
        }}
        onClick={onClick}
      >
        <div style={{ fontSize: '32px', color: colors.grey[400], marginBottom: '8px' }}>+</div>
        <div style={{ fontSize: '14px', color: colors.grey[300], fontWeight: 500 }}>Add Account</div>
      </Card>
    );
  }


  const isInactive = !isActive;

  return (
    <Card
      active={isActive}
      theme={theme}
      isInactive={isInactive && !isEditMode}
      isEditMode={isEditMode}
      onClick={() => {
        if (isEditMode && isInactive) {
          onActivate(account);
        }
      }}
    >
      <CardHeader>
        <CardTitle theme={theme}>{account.name}</CardTitle>
        <StatusBadge active={isActive} theme={theme}>
          {isActive ? 'Active' : 'Inactive'}
        </StatusBadge>

      </CardHeader>

      <Balance theme={theme}>{formatCurrency(account.initialBalance || account.initial_balance || account.balance, account.currency)}</Balance>

      <CardDetails>
        <DetailRow theme={theme}>
          <span>Type</span>
          <span>{account.account_type || 'Firm'}</span>
        </DetailRow>
        <DetailRow theme={theme}>
          <span>Broker/Firm</span>
          <span>{account.broker || '-'}</span>
        </DetailRow>
        <DetailRow theme={theme}>
          <span>Platform</span>
          <span>{account.platform || '-'}</span>
        </DetailRow>
      </CardDetails>

      {isEditMode && (
        <Actions>
          {!isActive ? (
            <>
              <Btn
                primary
                theme={theme}
                onClick={(e) => {
                  e.stopPropagation();
                  onActivate(account);
                }}
                style={{ pointerEvents: 'auto', flex: 1.5 }}
              >
                Switch
              </Btn>
              <Btn
                theme={theme}
                onClick={(e) => {
                  e.stopPropagation();
                  onManage(account);
                }}
                style={{ pointerEvents: 'auto', flex: 1.5 }}
              >
                Details
              </Btn>
              <Btn
                theme={theme}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onDelete) onDelete(account);
                }}
                style={{
                  pointerEvents: 'auto',
                  flex: 1,
                  color: tokens(theme.palette.mode).redAccent[500],
                  borderColor: tokens(theme.palette.mode).redAccent[500],
                  border: `1px solid ${tokens(theme.palette.mode).redAccent[500]}`,
                }}
                title="Delete Account"
              >
                ✕
              </Btn>
            </>
          ) : (
            <Btn
              fullWidth
              theme={theme}
              onClick={(e) => {
                e.stopPropagation();
                onManage(account);
              }}
            >
              Manage
            </Btn>
          )}
        </Actions>
      )}
    </Card>
  );
};

export default AccountCard;
